import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import { scrollToForm, trackEvent, trackPlanSelected, trackAddonSelected, SERVICE } from "@/lib/tracking";

export const PLANS = [
  {
    id: "dwy",
    name: "DWY Paid Ads Enablement",
    price: "R4,850",
    period: "p/m",
    priceLead: "From",
    description:
      "Guidance, training and support while your internal team executes.",
    includes: [
      "Paid ads strategy guidance",
      "Campaign structure guidance",
      "Platform selection guidance",
      "Audience and targeting guidance",
      "Monthly review session",
      "Campaign improvement recommendations",
      "Basic performance review",
      "Training on what to adjust and why",
    ],
    bestFor:
      "Internal teams, founders, marketers and SMEs that want to learn while implementing.",
    color: "#4285f4",
  },
  {
    id: "dfy",
    name: "DFY Paid Ads Management",
    price: "R8,890",
    period: "p/m",
    priceLead: "From",
    description:
      "Rekisa sets up, manages and optimises paid ads on one main platform.",
    includes: [
      "Paid ads strategy",
      "One primary ad platform",
      "Campaign setup and management",
      "Audience targeting",
      "Basic remarketing setup where possible",
      "Conversion tracking guidance",
      "Monthly optimisation",
      "Monthly performance report",
      "Recommendations to improve lead quality",
    ],
    bestFor:
      "Businesses that want a managed paid ads service without an internal ads team.",
    highlight: true,
    color: "#1a73e8",
  },
  {
    id: "growth",
    name: "Growth Paid Ads System",
    price: "R13,500",
    period: "p/m",
    priceLead: "From",
    description:
      "A stronger paid ads system across multiple channels with remarketing and tracking.",
    includes: [
      "Paid ads strategy",
      "Multi-platform campaign management",
      "Google, Meta, or LinkedIn campaign mix",
      "Remarketing campaigns",
      "Conversion tracking setup guidance",
      "Campaign testing",
      "Search term, audience, placement, and creative review",
      "Monthly optimisation",
      "Monthly performance report",
      "Lead quality recommendations",
    ],
    bestFor: "Businesses that want to scale paid ads beyond one channel.",
    color: "#34a853",
  },
];

export const ADDONS = [
  {
    id: "landing_page",
    eventKey: "addon_landing_page_selected",
    label: "Add Landing Page Setup",
    price: "From R12,500 once-off",
    desc: "A focused campaign landing page that matches the ad and turns visitors into enquiries.",
    color: "#4285f4",
  },
  {
    id: "followup",
    eventKey: "addon_followup_selected",
    label: "Add Automated Follow-Up Setup",
    price: "From R8,500 once-off",
    desc: "Follow-up via WhatsApp, email, CRM workflows, SMS — whatever suits your sales process.",
    color: "#ea4335",
  },
  {
    id: "whatsapp",
    eventKey: "addon_whatsapp_selected",
    label: "Add WhatsApp Automation Management",
    price: "From R4,850 p/m",
    desc: "Ongoing WhatsApp automation support, flow updates, lead capture and campaign support.",
    color: "#fbbc04",
  },
  {
    id: "extra_platform",
    eventKey: "addon_extra_platform_selected",
    label: "Add Another Ad Platform",
    price: "From R4,850 p/m per platform",
    desc: "Add management for another platform such as Google, Meta or LinkedIn.",
    color: "#34a853",
  },
];

const PLAN_BADGE = { dwy: "Done with you", dfy: "Done for you" };
function planBadgeLabel(planId) {
  return PLAN_BADGE[planId] || "Multi-channel";
}

export default function PricingSection({ selectedPlan, setSelectedPlan, addons, setAddons }) {
  const handleSelect = (planId) => {
    setSelectedPlan(planId);
    trackEvent("pricing_plan_selected", { plan: planId });
    trackEvent("cta_click", { location: "pricing", plan: planId });
    trackPlanSelected({ service: SERVICE.paid_ads, planId });
    scrollToForm();
  };

  const toggleAddon = (id, eventKey) => {
    setAddons((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      if (next[id]) {
        trackEvent(eventKey, { addon: id });
        trackAddonSelected({ service: SERVICE.paid_ads, addonId: id });
      }
      return next;
    });
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 sm:py-32 bg-white"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl mb-12">
          <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-4">Pricing Plans</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-[-0.03em] leading-[1.05] text-zinc-900">
            Choose your level of support.
          </h2>
          <p className="mt-5 text-zinc-600 max-w-2xl text-lg">
            Three flexible plans. Toggle add-ons below to tell us exactly what you want included.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          {PLANS.map((plan) => {
            const isHighlight = plan.highlight;
            return (
              <div
                key={plan.id}
                data-testid={`pricing-card-${plan.id}`}
                className={`relative rounded-3xl p-8 flex flex-col transition-all ${
                  isHighlight
                    ? "bg-gradient-to-b from-[#e8f0fe] to-white border-2 border-[#1a73e8] shadow-[0_30px_80px_-30px_rgba(26,115,232,0.4)] scale-[1.02]"
                    : "border border-zinc-200 bg-white hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.15)] hover:border-zinc-300"
                }`}
              >
                {isHighlight && (
                  <div className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-[#1a73e8] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white shadow-sm">
                    <Sparkles className="h-3 w-3" /> Most Popular
                  </div>
                )}

                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ background: plan.color }}
                  />
                  <span className="text-[11px] tracking-[0.18em] uppercase font-mono text-zinc-500 font-semibold">
                    {planBadgeLabel(plan.id)}
                  </span>
                </div>

                <h3 className="font-display text-2xl font-black text-zinc-900 tracking-tight">{plan.name}</h3>
                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-xs text-zinc-500 uppercase tracking-widest font-mono">{plan.priceLead}</span>
                  <span className="font-display text-5xl font-black text-zinc-900 tracking-tight">{plan.price}</span>
                  <span className="text-zinc-500 text-sm">{plan.period}</span>
                </div>
                <p className="mt-4 text-sm text-zinc-600 leading-relaxed">{plan.description}</p>

                <Button
                  onClick={() => handleSelect(plan.id)}
                  data-testid={`pricing-cta-${plan.id}`}
                  className={`mt-6 h-12 rounded-full text-base font-semibold transition-all ${
                    isHighlight
                      ? "bg-[#1a73e8] hover:bg-[#1557b0] text-white shadow-[0_8px_24px_rgba(26,115,232,0.4)]"
                      : "bg-zinc-900 hover:bg-black text-white shadow-sm"
                  }`}
                >
                  Request a Quote
                  <ArrowUpRight className="ml-1 h-5 w-5" />
                </Button>

                <div className="mt-7 space-y-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono font-semibold">
                    What's included
                  </div>
                  <ul className="space-y-2.5">
                    {plan.includes.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-sm text-zinc-800">
                        <span
                          className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                          style={{ background: `${plan.color}15`, color: plan.color }}
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-7 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-2 font-semibold">Best for</div>
                  <p className="text-sm text-zinc-700 leading-relaxed">{plan.bestFor}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add-on toggles */}
        <div className="mt-16">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-6 gap-2">
            <div>
              <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 font-mono mb-2">Add-ons</div>
              <h3 className="font-display text-3xl sm:text-4xl font-black text-zinc-900 tracking-tight">
                Want more included?
              </h3>
            </div>
            <div className="text-sm text-zinc-500 max-w-sm">
              Toggle any add-on. Your selection is sent with the quote form below.
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {ADDONS.map((addon) => (
              <label
                key={addon.id}
                htmlFor={`addon-${addon.id}`}
                data-testid={`addon-row-${addon.id}`}
                className={`group flex items-start justify-between gap-5 rounded-2xl border p-5 cursor-pointer transition-all ${
                  addons[addon.id]
                    ? "border-zinc-300 bg-zinc-50 shadow-sm"
                    : "border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-sm"
                }`}
                style={addons[addon.id] ? { boxShadow: `0 0 0 1px ${addon.color}40, 0 4px 16px ${addon.color}15` } : {}}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="h-2 w-2 rounded-full shrink-0"
                      style={{ background: addon.color }}
                    />
                    <span className="font-display text-base sm:text-lg font-bold text-zinc-900">{addon.label}</span>
                    <span
                      className="text-[11px] font-mono rounded-full px-2.5 py-0.5 font-semibold"
                      style={{ color: addon.color, background: `${addon.color}12`, border: `1px solid ${addon.color}30` }}
                    >
                      {addon.price}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">{addon.desc}</p>
                </div>
                <Switch
                  id={`addon-${addon.id}`}
                  data-testid={`addon-toggle-${addon.id}`}
                  checked={!!addons[addon.id]}
                  onCheckedChange={() => toggleAddon(addon.id, addon.eventKey)}
                  className="data-[state=checked]:bg-[#1a73e8] mt-1"
                />
              </label>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
